import { Fragment, useState, useEffect, useMemo } from 'react';
import { ManagedFile, FileHierarchy } from '../../types';
import { getManagedFiles, getFileHierarchy, deleteManagedFile, updateManagedFile, formatFileSize, shareFile, unshareFile, updateSharePolicy, getPublicShareUrl, ShareOptions } from '../../api/files';
import { useAuth } from '../../hooks/useAuth';
import { UploadModal } from '../../components/files/UploadModal';
import { ActionSheet } from '../../components/ui/ActionMenu';
import { AudioPlayer } from '../../components/audio/AudioPlayer';
import { VideoPlayer } from '../../components/video/VideoPlayer';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Loading } from '../../components/ui/Loading';
import { SideSheet, ConfirmationModal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import {
  FileTree,
  FilesHighlight,
  FolderItem,
  FolderHeader,
  FolderTrigger,
  FolderContent,
  FileHighlight,
  FileItem,
  FileIcon,
  FileLabel,
  FolderIcon,
  ChevronIcon,
} from '../../components/animate-ui';

// Icons
const FolderClosedIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const FolderOpenIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
  </svg>
);

const ZipIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
  </svg>
);

const PlayCircleIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ShareIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

interface HierarchyWithFiles extends FileHierarchy {
  vibes: {
    id: string;
    name: string;
    slug: string;
    image: string | null;
    cuts: {
      id: string;
      name: string;
      slug: string;
      files: ManagedFile[];
    }[];
  }[];
}

// Context for currently playing file
interface PlayingContext {
  file: ManagedFile;
  vibeName: string;
  vibeImage: string | null;
  projectName: string;
  projectImage: string | null;
}

export function FileExplorer() {
  const { canModifyFile } = useAuth();
  const [hierarchy, setHierarchy] = useState<HierarchyWithFiles[]>([]);
  const [files, setFiles] = useState<ManagedFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Expanded state - now combined into a single array for FileTree
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadTargetCutId, setUploadTargetCutId] = useState<string | undefined>();

  // Edit modal
  const [editingFile, setEditingFile] = useState<ManagedFile | null>(null);
  const [editName, setEditName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete confirmation
  const [deletingFile, setDeletingFile] = useState<ManagedFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Stem file action sheet
  const [stemActionFile, setStemActionFile] = useState<ManagedFile | null>(null);

  // Share modal
  const [sharingFile, setSharingFile] = useState<ManagedFile | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bandmateVisible, setBandmateVisible] = useState(false);
  const [publicComments, setPublicComments] = useState(false);

  // Sync policy checkboxes when sharingFile changes
  useEffect(() => {
    if (sharingFile) {
      setBandmateVisible(sharingFile.bandmateCommentsVisible ?? false);
      setPublicComments(sharingFile.publicCommentsEnabled ?? false);
    }
  }, [sharingFile]);

  // Audio player
  const [playingContext, setPlayingContext] = useState<PlayingContext | null>(null);

  // Video player
  const [playingVideoContext, setPlayingVideoContext] = useState<PlayingContext | null>(null);

  const refreshHierarchy = async () => {
    try {
      const [filesData, hierarchyData] = await Promise.all([
        getManagedFiles(),
        getFileHierarchy(),
      ]);
      setFiles(filesData);
      
      // Merge files into hierarchy
      const hierarchyWithFiles: HierarchyWithFiles[] = hierarchyData.map(project => ({
        ...project,
        vibes: project.vibes.map(vibe => ({
          ...vibe,
          cuts: vibe.cuts.map(cut => ({
            ...cut,
            files: filesData.filter(f => f.cutId === cut.id),
          })),
        })),
      }));
      
      setHierarchy(hierarchyWithFiles);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    await refreshHierarchy();
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Compute all possible IDs for expand/collapse all
  const allFolderIds = useMemo(() => {
    const ids: string[] = [];
    hierarchy.forEach(project => {
      ids.push(`project-${project.id}`);
      project.vibes.forEach(vibe => {
        ids.push(`vibe-${vibe.id}`);
        vibe.cuts.forEach(cut => {
          ids.push(`cut-${cut.id}`);
        });
      });
    });
    return ids;
  }, [hierarchy]);

  const handleUploadToCut = (cutId: string) => {
    setUploadTargetCutId(cutId);
    setIsUploadModalOpen(true);
  };

  const handleEdit = (file: ManagedFile) => {
    setEditingFile(file);
    setEditName(file.name || '');
  };

  const handleSaveEdit = async () => {
    if (!editingFile) return;
    
    setIsUpdating(true);
    try {
      await updateManagedFile(editingFile.id, {
        name: editName || undefined,
      });
      setEditingFile(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update file:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingFile) return;
    
    setIsDeleting(true);
    try {
      await deleteManagedFile(deletingFile.id);
      setDeletingFile(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete file:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = (file: ManagedFile) => {
    const link = document.createElement('a');
    link.href = `/${file.path}`;
    link.download = file.originalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = (file: ManagedFile) => {
    setSharingFile(file);
    setCopied(false);
  };

  const handleToggleShare = async () => {
    if (!sharingFile) return;
    setIsSharing(true);
    try {
      if (sharingFile.isPublic) {
        const updated = await unshareFile(sharingFile.id);
        setSharingFile(updated);
      } else {
        const updated = await shareFile(sharingFile.id);
        setSharingFile(updated);
      }
      fetchData();
    } catch (error) {
      console.error('Failed to toggle share:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!sharingFile?.shareToken) return;
    await navigator.clipboard.writeText(getPublicShareUrl(sharingFile.shareToken));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePolicyChange = async (field: keyof ShareOptions, value: boolean) => {
    if (!sharingFile?.id) return;
    // Optimistically update local state
    if (field === 'bandmateCommentsVisible') setBandmateVisible(value);
    else setPublicComments(value);
    try {
      const updated = await updateSharePolicy(sharingFile.id, { [field]: value });
      setSharingFile(updated);
    } catch (error) {
      console.error('Failed to update share policy:', error);
      // Revert on failure
      if (field === 'bandmateCommentsVisible') setBandmateVisible(!value);
      else setPublicComments(!value);
    }
  };

  const handlePlayFile = (file: ManagedFile, vibeName: string, vibeImage: string | null, projectName: string, projectImage: string | null) => {
    setPlayingContext({
      file,
      vibeName,
      vibeImage,
      projectName,
      projectImage,
    });
  };

  const handlePlayVideoFile = (file: ManagedFile, vibeName: string, vibeImage: string | null, projectName: string, projectImage: string | null) => {
    setPlayingVideoContext({
      file,
      vibeName,
      vibeImage,
      projectName,
      projectImage,
    });
  };

  const handleClosePlayer = () => {
    setPlayingContext(null);
  };

  const handleCloseVideoPlayer = () => {
    setPlayingVideoContext(null);
  };

  const expandAll = () => {
    setExpandedItems(allFolderIds);
  };

  const collapseAll = () => {
    setExpandedItems([]);
  };

  if (isLoading) {
    return <Loading className="py-12" />;
  }

  const totalFiles = files.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-text">File Explorer</h1>
          <p className="text-muted mt-1">
            {totalFiles} file{totalFiles !== 1 ? 's' : ''} across {hierarchy.length} project{hierarchy.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="ghost" onClick={collapseAll}>
            Collapse All
          </Button>
          <Button onClick={() => { setUploadTargetCutId(undefined); setIsUploadModalOpen(true); }}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload File
          </Button>
        </div>
      </div>

      {/* Tree View */}
      {hierarchy.length === 0 ? (
        <Card className="text-center py-12">
          <FolderClosedIcon className="w-16 h-16 text-muted mx-auto mb-4" />
          <p className="text-text font-medium">No projects found</p>
          <p className="text-sm text-muted mt-1">
            Create a project, vibe, and cut to start organizing files.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <FileTree
            value={expandedItems}
            onValueChange={setExpandedItems}
            highlightClassName="bg-surface-light"
            highlightHover={false}
          >
            <FilesHighlight>
              {hierarchy.map(project => (
                <FolderItem key={project.id} id={`project-${project.id}`}>
                  {/* Project Row */}
                  <FolderHeader>
                    <FileHighlight>
                      <FolderTrigger className="gap-2 p-3 hover:bg-surface-light/50 w-full transition-colors">
                        <ChevronIcon className="text-muted" />
                        <FolderIcon 
                          className="text-yellow-500"
                          openIcon={<FolderOpenIcon className="w-5 h-5" />}
                          closedIcon={<FolderClosedIcon className="w-5 h-5" />}
                        />
                        <FileLabel className="font-medium text-text flex-1 text-left">{project.name}</FileLabel>
                        <span className="text-xs text-muted">
                          {project.vibes.length} vibe{project.vibes.length !== 1 ? 's' : ''}
                        </span>
                      </FolderTrigger>
                    </FileHighlight>
                  </FolderHeader>

                  {/* Vibes */}
                  <FolderContent className="border-t border-border/50">
                    <div className="bg-surface/30">
                      {project.vibes.length === 0 ? (
                        <div className="pl-10 pr-3 py-2 text-sm text-muted italic">
                          No vibes in this project
                        </div>
                      ) : (
                        project.vibes.map(vibe => (
                          <FolderItem key={vibe.id} id={`vibe-${vibe.id}`}>
                            {/* Vibe Row */}
                            <FolderHeader>
                              <FileHighlight>
                                <FolderTrigger className="gap-2 pl-8 pr-3 py-2 hover:bg-surface-light/50 w-full transition-colors">
                                  <ChevronIcon className="text-muted" />
                                  <FolderIcon 
                                    className="text-blue-400"
                                    openIcon={<FolderOpenIcon className="w-5 h-5" />}
                                    closedIcon={<FolderClosedIcon className="w-5 h-5" />}
                                  />
                                  <FileLabel className="text-text flex-1 text-left">{vibe.name}</FileLabel>
                                  <span className="text-xs text-muted">
                                    {vibe.cuts.length} cut{vibe.cuts.length !== 1 ? 's' : ''}
                                  </span>
                                </FolderTrigger>
                              </FileHighlight>
                            </FolderHeader>

                            {/* Cuts */}
                            <FolderContent>
                              <div>
                                {vibe.cuts.length === 0 ? (
                                  <div className="pl-16 pr-3 py-2 text-sm text-muted italic">
                                    No cuts in this vibe
                                  </div>
                                ) : (
                                  vibe.cuts.map(cut => (
                                    <FolderItem key={cut.id} id={`cut-${cut.id}`}>
                                     {/* Cut Row */}
                                     <div className="flex items-center">
                                       <FileHighlight className="flex-1">
                                         <div>
                                           <FolderTrigger className="gap-2 pl-14 pr-3 py-2 hover:bg-surface-light/50 w-full transition-colors">
                                             <ChevronIcon className="text-muted" />
                                             <FolderIcon 
                                               className="text-primary"
                                               openIcon={<FolderOpenIcon className="w-5 h-5" />}
                                               closedIcon={<FolderClosedIcon className="w-5 h-5" />}
                                             />
                                             <FileLabel className="text-text flex-1 text-left">{cut.name}</FileLabel>
                                             <span className="text-xs text-muted">
                                               {cut.files.length} file{cut.files.length !== 1 ? 's' : ''}
                                             </span>
                                           </FolderTrigger>
                                         </div>
                                       </FileHighlight>
                                       <button
                                         onClick={(e) => { e.stopPropagation(); handleUploadToCut(cut.id); }}
                                         className="p-1 text-muted hover:text-primary hover:bg-primary/10 rounded transition-colors mr-3"
                                         title="Upload to this cut"
                                       >
                                         <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                         </svg>
                                       </button>
                                     </div>

                                      {/* Files */}
                                      <FolderContent>
                                        <div className="bg-background/30">
                                          {cut.files.length === 0 ? (
                                            <div className="pl-8 sm:pl-12 md:pl-20 pr-1 sm:pr-3 py-2 text-sm text-muted italic flex items-center gap-2">
                                              <span>No files yet</span>
                                              <button
                                                onClick={() => handleUploadToCut(cut.id)}
                                                className="text-primary hover:underline"
                                              >
                                                Upload one
                                              </button>
                                            </div>
                                          ) : (
                                            cut.files.map(file => (
                                              <Fragment key={file.id}>
                                                <FileHighlight>
                                                  <FileItem
                                                    className={`gap-1 sm:gap-2 pl-8 sm:pl-12 md:pl-20 pr-1 sm:pr-3 py-2 hover:bg-surface-light/50 group cursor-pointer transition-colors ${
                                                      playingContext?.file.id === file.id || playingVideoContext?.file.id === file.id ? 'bg-purple-500/5' : ''
                                                    }`}
                                                    onClick={() => {
                                                      if (file.type === 'CUT') {
                                                        handlePlayFile(file, vibe.name, vibe.image, project.name, project.image);
                                                      } else if (file.type === 'VIDEO_FILE') {
                                                        handlePlayVideoFile(file, vibe.name, vibe.image, project.name, project.image);
                                                      } else if (file.type === 'STEM') {
                                                        setStemActionFile(file);
                                                      }
                                                    }}
                                                  >
                                                    {file.type === 'CUT' || file.type === 'VIDEO_FILE' ? (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (file.type === 'VIDEO_FILE') {
                                                            handlePlayVideoFile(file, vibe.name, vibe.image, project.name, project.image);
                                                          } else {
                                                            handlePlayFile(file, vibe.name, vibe.image, project.name, project.image);
                                                          }
                                                        }}
                                                        className={`flex-shrink-0 transition-colors ${
                                                          playingContext?.file.id === file.id || playingVideoContext?.file.id === file.id
                                                            ? 'text-purple-400'
                                                            : 'text-muted hover:text-purple-400'
                                                        }`}
                                                        title="Play"
                                                      >
                                                        <PlayCircleIcon className="w-5 h-5" />
                                                      </button>
                                                    ) : (
                                                      <FileIcon>
                                                        <ZipIcon className="w-4 h-4 text-blue-400" />
                                                      </FileIcon>
                                                    )}
                                                    <FileLabel className="text-sm text-text flex-1" title={file.originalName}>
                                                      {file.name || file.originalName}
                                                    </FileLabel>
                                                    <span className="text-xs text-muted flex-shrink-0">
                                                      {formatFileSize(file.fileSize)}
                                                    </span>
                                                    <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                                      file.type === 'CUT'
                                                        ? 'bg-primary/20 text-primary'
                                                        : file.type === 'VIDEO_FILE'
                                                        ? 'bg-purple-500/20 text-purple-400'
                                                        : 'bg-blue-500/20 text-blue-400'
                                                    }`}>
                                                      {file.type === 'VIDEO_FILE' ? 'VIDEO' : file.type}
                                                    </span>
                                                   
                                                    {/* File Actions */}
                                                    <div className="max-sm:hidden flex items-center gap-1">
                                                      <button
                                                        onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                                                        className="p-1 text-muted hover:text-primary hover:bg-primary/10 rounded transition-colors"
                                                        title="Download"
                                                      >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                        </svg>
                                                      </button>
                                                      <button
                                                        onClick={(e) => { e.stopPropagation(); handleEdit(file); }}
                                                        className="p-1 text-muted hover:text-primary hover:bg-primary/10 rounded transition-colors"
                                                        title="Edit"
                                                      >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                      </button>
                                                      <button
                                                        onClick={(e) => { e.stopPropagation(); setDeletingFile(file); }}
                                                        className="p-1 text-muted hover:text-error hover:bg-error/10 rounded transition-colors"
                                                        title="Delete"
                                                      >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                      </button>
                                                    </div>
                                                  </FileItem>
                                                </FileHighlight>

                                                {/* Inline Audio Player */}
                                                {playingContext?.file.id === file.id && (
                                                  <div className="border-t border-border/50 py-3">
                                                    <AudioPlayer
                                                      audioUrl={`/${playingContext.file.path}`}
                                                      trackName={playingContext.file.name || playingContext.file.originalName}
                                                      vibeImage={playingContext.vibeImage}
                                                      vibeName={playingContext.vibeName}
                                                      projectName={playingContext.projectName}
                                                      projectImage={playingContext.projectImage}
                                                      onClose={handleClosePlayer}
                                                    />
                                                  </div>
                                                )}

                                                {/* Inline Video Player */}
                                                {playingVideoContext?.file.id === file.id && (
                                                  <div className="border-t border-border/50 py-3 relative">
                                                    <button
                                                      onClick={handleCloseVideoPlayer}
                                                      className="absolute top-3 right-3 z-10 p-1.5 bg-surface-light hover:bg-surface rounded-full text-muted hover:text-text transition-colors"
                                                      title="Close"
                                                    >
                                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                      </svg>
                                                    </button>
                                                    <VideoPlayer
                                                      src={`/${playingVideoContext.file.path}`}
                                                      title={playingVideoContext.file.name || playingVideoContext.file.originalName}
                                                    />
                                                  </div>
                                                )}
                                              </Fragment>
                                            ))
                                          )}
                                        </div>
                                      </FolderContent>
                                    </FolderItem>
                                  ))
                                )}
                              </div>
                            </FolderContent>
                          </FolderItem>
                        ))
                      )}
                    </div>
                  </FolderContent>
                </FolderItem>
              ))}
            </FilesHighlight>
          </FileTree>
        </Card>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => { setIsUploadModalOpen(false); setUploadTargetCutId(undefined); }}
        onSuccess={refreshHierarchy}
        hierarchy={hierarchy}
        defaultCutId={uploadTargetCutId}
      />

      {/* Edit File Side Sheet */}
      <SideSheet
        isOpen={!!editingFile}
        onClose={() => setEditingFile(null)}
        title="Edit File"
        description="Update file name and details"
        footer={
          <Button onClick={handleSaveEdit} isLoading={isUpdating}>
            Save Changes
          </Button>
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            placeholder="Enter a name..."
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
          
          {editingFile?.cut && (
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">
                Location
              </label>
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <span className="px-2 py-1 bg-surface-light rounded text-muted">
                  {editingFile.cut.vibe.project.name}
                </span>
                <span className="text-muted">/</span>
                <span className="px-2 py-1 bg-surface-light rounded text-muted">
                  {editingFile.cut.vibe.name}
                </span>
                <span className="text-muted">/</span>
                <span className="px-2 py-1 bg-primary/10 rounded text-primary">
                  {editingFile.cut.name}
                </span>
              </div>
            </div>
          )}
        </div>
      </SideSheet>

      {/* Stem File Action Sheet */}
      <ActionSheet
        title={stemActionFile?.name || stemActionFile?.originalName || 'File'}
        open={!!stemActionFile}
        onOpenChange={(open) => { if (!open) setStemActionFile(null); }}
        hideTrigger
        items={[
          {
            label: 'Download',
            description: 'Download file to your device',
            icon: <DownloadIcon />,
            onClick: () => { if (stemActionFile) handleDownload(stemActionFile); },
          },
          {
            label: stemActionFile?.isPublic ? 'Manage Share' : 'Share',
            description: stemActionFile?.isPublic ? 'View or revoke share link' : 'Create a share link',
            icon: <ShareIcon />,
            onClick: () => { if (stemActionFile) handleShare(stemActionFile); },
            visible: stemActionFile ? canModifyFile(stemActionFile) : false,
          },
          {
            label: 'Edit',
            description: 'Update file name and details',
            icon: <EditIcon />,
            onClick: () => { if (stemActionFile) handleEdit(stemActionFile); },
            visible: stemActionFile ? canModifyFile(stemActionFile) : false,
          },
          {
            label: 'Delete',
            description: 'Permanently remove this file',
            icon: <DeleteIcon />,
            onClick: () => { if (stemActionFile) setDeletingFile(stemActionFile); },
            variant: 'danger',
            visible: stemActionFile ? canModifyFile(stemActionFile) : false,
          },
        ]}
      />

      {/* Share File Side Sheet */}
      <SideSheet
        isOpen={!!sharingFile}
        onClose={() => setSharingFile(null)}
        title="Share File"
        description="Manage public sharing settings"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted mb-1">File</label>
            <p className="text-text">{sharingFile?.name || sharingFile?.originalName}</p>
          </div>
          <div className="flex items-center justify-between p-4 bg-surface-light rounded-lg">
            <div>
              <p className="text-sm font-medium text-text">Public Sharing</p>
              <p className="text-xs text-muted mt-0.5">
                {sharingFile?.isPublic ? 'Anyone with the link can access this file' : 'Only project members can access this file'}
              </p>
            </div>
            <Button variant={sharingFile?.isPublic ? 'danger' : 'primary'} size="sm" onClick={handleToggleShare} isLoading={isSharing}>
              {sharingFile?.isPublic ? 'Make Private' : 'Make Public'}
            </Button>
          </div>
          {sharingFile?.isPublic && sharingFile?.shareToken && (
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">Share Link</label>
              <div className="flex gap-2">
                <input type="text" readOnly value={getPublicShareUrl(sharingFile.shareToken)}
                  className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text truncate" />
                <Button variant="secondary" onClick={handleCopyLink} className="flex-shrink-0">
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  <span className="ml-2">{copied ? 'Copied!' : 'Copy'}</span>
                </Button>
              </div>
            </div>
          )}

          {/* Comment policy toggles - only when shared */}
          {sharingFile?.isPublic && (
            <div className="space-y-3 pt-2 border-t border-border">
              <p className="text-sm font-medium text-text">Comment Policy</p>

              <label className="flex items-center justify-between cursor-pointer group">
                <div className="flex-1">
                  <p className="text-sm text-text">Bandmate comments visible</p>
                  <p className="text-xs text-muted">Project members can see comments on the public page</p>
                </div>
                <input
                  type="checkbox"
                  checked={bandmateVisible}
                  onChange={(e) => handlePolicyChange('bandmateCommentsVisible', e.target.checked)}
                  className="ml-3 w-4 h-4 rounded border-border text-primary focus:ring-primary/30 cursor-pointer"
                />
              </label>

              <label className="flex items-center justify-between cursor-pointer group">
                <div className="flex-1">
                  <p className="text-sm text-text">Public comments enabled</p>
                  <p className="text-xs text-muted">Anyone with the link can post comments as a guest</p>
                </div>
                <input
                  type="checkbox"
                  checked={publicComments}
                  onChange={(e) => handlePolicyChange('publicCommentsEnabled', e.target.checked)}
                  className="ml-3 w-4 h-4 rounded border-border text-primary focus:ring-primary/30 cursor-pointer"
                />
              </label>
            </div>
          )}
        </div>
      </SideSheet>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deletingFile}
        onClose={() => setDeletingFile(null)}
        title="Delete File"
        description="This action cannot be undone"
      >
        <div className="space-y-4">
          <p className="text-muted">
            Are you sure you want to delete{' '}
            <span className="text-text font-medium">
              {deletingFile?.name || deletingFile?.originalName}
            </span>
            ?
          </p>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button variant="ghost" onClick={() => setDeletingFile(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isDeleting}>
              Delete
            </Button>
          </div>
        </div>
      </ConfirmationModal>


    </div>
  );
}
